import { JobApplication, ClientLead, MasterProfile } from '../types';

const APPS_STORAGE_KEY = 'job_hunt_applications_v1';
const PROFILE_STORAGE_KEY = 'job_hunt_master_profile_v1';
const LEADS_STORAGE_KEY = 'job_hunt_client_leads_v1';

export const defaultMasterProfile: MasterProfile = {
  fullName: "Alex Taylor",
  title: "Cybersecurity & Information Security Specialist",
  email: "alex.taylor.infosec@example.co.uk",
  phone: "+44 7700 900123",
  location: "London / Remote UK",
  linkedinUrl: "https://linkedin.com/in/alex-taylor-infosec",
  githubUrl: "https://github.com/alex-taylor-sec",
  portfolioUrl: "https://alextaylor-infosec.co.uk",
  summary: "Results-oriented Cybersecurity & Information Security Specialist with 6+ years of hands-on experience supporting UK Managed Service Providers (MSPs), enterprises, and public sector clients. Expertise in Cyber Essentials Plus audits, vulnerability management, SIEM log monitoring (Sentinel, Splunk), incident response, and Microsoft 365 / Entra ID security hardening. Available for immediate UK contract & MSP overflow support.",
  skills: [
    "Cybersecurity", "Information Security", "Cyber Essentials Plus", "ISO 27001",
    "MSP Security Operations", "SIEM / SOC Monitoring", "Microsoft Sentinel", "Splunk",
    "Incident Response", "Vulnerability Management (Nessus / Qualys)", "Microsoft 365 & Entra ID Security",
    "Endpoint Protection (EDR / CrowdStrike / Defender)", "Firewall Configuration", "Network Security",
    "Active Directory & Zero Trust", "Security Auditing & Compliance"
  ],
  experiences: [
    {
      id: "exp_1",
      role: "Lead Cybersecurity & MSP Security Consultant",
      company: "Apex MSP Cloud Solutions",
      period: "2022 - Present",
      location: "London, UK (Remote)",
      bullets: [
        "Delivered end-to-end security audits, Cyber Essentials Plus readiness assessments, and vulnerability remediations for 35+ UK SME clients across MSP contracts.",
        "Engineered automated Microsoft Sentinel threat detection rules and EDR policies, cutting incident response times by 60%.",
        "Conducted emergency ransomware prevention protocols, firewall policy hardening, and Microsoft 365 tenant security audits with zero client downtime."
      ]
    },
    {
      id: "exp_2",
      role: "Information Security & SOC Analyst",
      company: "Vanguard Cyber Defense UK",
      period: "2019 - 2022",
      location: "Manchester, UK",
      bullets: [
        "Monitored multi-tenant SIEM logs and executed threat triage across 200+ endpoints and cloud environments.",
        "Executed weekly vulnerability scans utilizing Qualys and Nessus, prioritizing critical CVE patch deployments with sysadmin teams.",
        "Authored ISO 27001 compliance documentation and delivered security awareness phishing simulations for corporate clients."
      ]
    }
  ],
  education: [
    {
      degree: "BSc (Hons) Cybersecurity & Network Security",
      institution: "University of Warwick",
      year: "2019"
    }
  ]
};

const initialSampleApplications: JobApplication[] = [
  {
    id: "app_1",
    company: "Vanguard Cyber Defense UK",
    role: "Contract Information Security & SOC Analyst",
    location: "Remote UK",
    salary_range: "£450 / day (Outside IR35)",
    job_type: "Contract",
    status: "interviewing",
    job_url: "https://jobserve.com/gb/en/Job-Search/?q=Cyber+Security",
    contact_person: "Mark Davies (Head of InfoSec)",
    contact_email: "mark.davies@vanguardcyber.example.co.uk",
    match_score: 96,
    tailored_cv_summary: "Information Security Analyst with extensive SIEM, vulnerability management, and incident response experience.",
    notes: "Technical interview passed. Discussing 3-month contract start date for Monday.",
    applied_date: "2026-08-22"
  },
  {
    id: "app_2",
    company: "Apex MSP Cloud Solutions",
    role: "MSP Security & Systems Overflow Engineer",
    location: "London / Remote UK",
    salary_range: "£375 / day",
    job_type: "Contract",
    status: "applied",
    job_url: "https://cord.co/",
    contact_person: "Sarah Jenkins (Technical Director)",
    contact_email: "s.jenkins@apexcloud.example.co.uk",
    match_score: 94,
    notes: "Sent MSP overflow pitch for Cyber Essentials Plus audit backlogs.",
    applied_date: "2026-08-24"
  }
];

export const storageService = {
  getProfile(): MasterProfile {
    try {
      const data = localStorage.getItem(PROFILE_STORAGE_KEY);
      return data ? JSON.parse(data) : defaultMasterProfile;
    } catch {
      return defaultMasterProfile;
    }
  },

  saveProfile(profile: MasterProfile): void {
    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch (e) {
      console.error("Failed to save profile locally", e);
    }
  },

  async getApplications(): Promise<JobApplication[]> {
    try {
      const res = await fetch('/api/applications');
      if (res.ok) {
        const json = await res.json();
        if (json.data && Array.isArray(json.data) && json.data.length > 0) {
          return json.data;
        }
      }
    } catch (e) {}

    try {
      const local = localStorage.getItem(APPS_STORAGE_KEY);
      if (local) {
        return JSON.parse(local);
      }
    } catch (e) {}

    return initialSampleApplications;
  },

  async saveApplication(app: JobApplication): Promise<JobApplication[]> {
    const apps = await this.getApplications();
    const existingIndex = apps.findIndex(a => a.id === app.id);
    let updated: JobApplication[];

    if (existingIndex >= 0) {
      updated = [...apps];
      updated[existingIndex] = { ...app, updated_at: new Date().toISOString() };
    } else {
      updated = [{ ...app, created_at: new Date().toISOString() }, ...apps];
    }

    try {
      localStorage.setItem(APPS_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error("Local save error", e);
    }

    try {
      fetch('/api/applications', {
        method: existingIndex >= 0 ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(app)
      }).catch(() => {});
    } catch {}

    return updated;
  },

  async deleteApplication(id: string): Promise<JobApplication[]> {
    const apps = await this.getApplications();
    const updated = apps.filter(a => a.id !== id);

    try {
      localStorage.setItem(APPS_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error("Local delete error", e);
    }

    try {
      fetch(`/api/applications?id=${id}`, { method: 'DELETE' }).catch(() => {});
    } catch {}

    return updated;
  },

  async saveClientLead(lead: Partial<ClientLead>): Promise<boolean> {
    const leadObj: ClientLead = {
      id: `lead_${Date.now()}`,
      clientName: lead.clientName || 'Inquirer',
      clientEmail: lead.clientEmail || '',
      companyName: lead.companyName,
      serviceRequested: lead.serviceRequested || 'MSP Security Audit',
      budgetEstimate: lead.budgetEstimate || '£500 - £1,500',
      urgency: lead.urgency || 'Standard',
      projectDescription: lead.projectDescription || '',
      status: 'new',
      createdAt: new Date().toISOString()
    };

    try {
      const existingStr = localStorage.getItem(LEADS_STORAGE_KEY);
      const existing: ClientLead[] = existingStr ? JSON.parse(existingStr) : [];
      localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify([leadObj, ...existing]));
    } catch {}

    try {
      await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadObj)
      });
      return true;
    } catch {
      return true;
    }
  }
};
